// NPM Imports
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cloneDeep } from "lodash";
import { Loader, Toastr, Button } from "@revvsales/components";

// Components Imports
import { ConfirmationBox } from "../../../commonComponents";
import TempWFRuleWrapper from "./TempWFRuleWrapper";
import AddAnotherRule from "./AddAnotherRule";

// Actions Imports
import { DocumentTemplateActions } from "../../../actions";

// Method Imports
import {
  validateTempWorkFlowFields,
  generatePayloadForTempWorkFlow,
} from "../../../actions/DocumentTemplateActions/templateMethod";

// Internals imports
import { WORKFLOW, EVENTS } from "../../../constants";
import { AmplitudeUtils } from "../../../utils";

// Destructuring
const {
  addNewRuleToTemplateWF,
  createTempWorkFlow,
  getInitData,
  removeRuleFromTemplateWF,
  removeWorkFlowFromTemplate,
  toggleWorkFlowScreen,
  updateTemplateWFCondition,
  updateUsedFields,
} = DocumentTemplateActions;
const { OPERATORS_REVERSE_MAPPING } = WORKFLOW;

class TempWorkFlow extends Component {
  state = {
    showButtonLoader: false,
    showConfirmationBox: false,
    showLoader: false,
  };

  componentDidMount() {
    const {
      entity: { wfId },
    } = this.props;
    if (wfId) {
      this._onInitTempWorkFlow(wfId);
    } else {
      this._createTempWorkflow();
    }
  }

  _toggleLoader = flag => {
    this.setState({ showLoader: flag });
  };

  _showToastr = (className, message) => {
    Toastr.showToastr({
      className,
      message,
      timeout: 2000,
    });
  };

  _createTempWorkflow = () => {
    const {
      entity: { dynamicFieldId },
      usedFields,
    } = this.props;
    const field = usedFields.find(data => data.id === dynamicFieldId);
    const payload = {
      active: true,
      description: "Template Clause workflow",
      entity: "DEAL",
      evaluate_all:
        (field.type === "CLAUSE" && field.meta.subtype === "multi_select") ||
        (field.type === "CLAUSE" && field.meta.subtype === "optional") ||
        field.type === "CHECKBOX",
      name: "Template Workflow",
      scope_type:
        field.type === "CLAUSE" ? "TEMPLATE_CLAUSE" : "TEMPLATE_DYNAMICFIELD",
      scopes: [
        {
          object_id: this.props.templateId,
          object_type: "TEMPLATE",
        },
      ],
    };
    this._toggleLoader(true);
    this.props
      .createTempWorkFlow(payload)
      .then(response => {
        this._onInitTempWorkFlow(response.id);
      })
      .catch(error => {
        console.error(`TempWorkFlow => _createTempWorkflow => ${error}`);
        this._toggleLoader(true);
      });
  };

  _onInitTempWorkFlow = id => {
    this._toggleLoader(true);
    this.props
      .getInitData(id)
      .then(() => {
        this._toggleLoader(false);
      })
      .catch(error => {
        console.error(`TemWorkFlow => _onInit => ${error}`);
        this._toggleLoader(false);
      });
  };

  _addRuleToWorkflow = () => {
    const { formFields, dynamicField } = this.props;
    const isValid = validateTempWorkFlowFields(formFields, dynamicField);
    if (isValid) {
      this.props.addNewRuleToTemplateWF(formFields);
    }
  };

  _removeRuleFromWorkflow = (data, index) => {
    this.props
      .removeRuleFromTemplateWF(this.props.formFields, data, index)
      .then(() => {
        Toastr.showToastr({
          type: "success",
          message: "Successfully removed the rule!",
          timeout: 2000,
        });
      })
      .catch(err => {
        console.error(
          `TempWorkFlow => _removeRuleFromWorkflow => removeRuleFromTemplateWF => ${err}`,
        );
      });
  };

  _detachWorkFlow = () => {
    const {
      usedFields,
      entity: { wfId },
    } = this.props;
    this.props
      .removeWorkFlowFromTemplate(wfId, usedFields)
      .then(() => {
        Toastr.showToastr({
          type: "success",
          message: "Successfully detached workflow!",
          timeout: 2000,
        });
      })
      .catch(err => {
        console.error(
          `TempWorkFlow => _detachWorkFlow => removeWorkFlowFromTemplate => ${err}`,
        );
      });
  };

  _onSubmit = () => {
    const {
      data,
      dynamicField,
      entity: { type = "" },
      formFields,
      metaData,
    } = this.props;
    /**
     * * Copy trigger message from action to condition, this is done as temp due to UI difference
     * * in template & workflow page whereas for backend it the same
     */
    formFields.forEach(rule => {
      const {
        tempWFAction: { triggerMessage },
        tempWFCondition: { conditions },
      } = rule;
      if (triggerMessage) {
        conditions.forEach(condition => {
          condition.trigger_message = triggerMessage;
          if (type === "DYNAMICFIELD") {
            condition.rel_op =
              OPERATORS_REVERSE_MAPPING[condition.rel_op] || condition.rel_op;
            delete condition.sub_param;
          }
        });
      }
    });

    const isValid = validateTempWorkFlowFields(formFields, dynamicField);

    if (isValid) {
      const isEdit = data.rules && data.rules.every(rule => rule.id);
      const payload = generatePayloadForTempWorkFlow(
        formFields,
        metaData,
        isEdit,
      );

      this.setState({ showButtonLoader: true });
      this.props
        .updateTemplateWFCondition(data.id, payload, metaData, data.scope_type)
        .then(response => {
          // Implementing Amplitude for template workflow  clause
          const templateWorkflowData = {
            ...response[0],
          };
          const workflowTempData = {};
          if (response && response.length > 0) {
            workflowTempData.rules = response.length;
          }
          if (templateWorkflowData.logical_op) {
            workflowTempData.operatorType = templateWorkflowData.logical_op;
          }
          if (templateWorkflowData.action && templateWorkflowData.action.data) {
            workflowTempData.approvalOrder =
              templateWorkflowData.action.data.type;
          }
          if (
            templateWorkflowData.action &&
            templateWorkflowData.action.data &&
            templateWorkflowData.action.data.levels &&
            templateWorkflowData.action.data.levels.length > 0
          ) {
            workflowTempData.approver =
              templateWorkflowData.action.data.levels[0].approvers ||
              templateWorkflowData.action.data.levels[0].group_approvers;
            if (
              templateWorkflowData.action.data.levels[0].group_approvers &&
              templateWorkflowData.action.data.levels[0].group_approvers
                .length > 0
            ) {
              workflowTempData.approverType = "Grouped";
            } else if (
              templateWorkflowData.action.data.levels[0].approvers &&
              templateWorkflowData.action.data.levels[0].approvers.length > 0
            ) {
              workflowTempData.approverType = "Individuals";
            }
          }
          AmplitudeUtils.amplitudeLogEvent(
            EVENTS.EVENT_NAMES.WORKFLOW_CLAUSE_OPTION_CREATED,
            workflowTempData,
          );
          this.setState({ showButtonLoader: false });
          if (!isEdit) {
            const fields = this._updateTempUsedFields();
            this.props.updateUsedFields(fields);
          }
          this._showToastr(
            "is-success",
            isEdit
              ? "Workflow has been updated successfully."
              : "Workflow has been saved successfully.",
          );
        })
        .catch(error => {
          this.setState({ showButtonLoader: false });
          console.error(
            `TempWorkFlow => _onSubmit => updateTemplateWFCondition => ${error}`,
          );
          this._showToastr(
            "is-failure",
            "Something went wrong. Please try again!",
          );
        });
    }
  };

  _updateTempUsedFields = () => {
    const {
      usedFields,
      entity: { dynamicFieldId: currentFieldId },
      data: { id: wfId },
    } = this.props;
    const fields = cloneDeep(usedFields);
    fields.forEach(field => {
      if (field.id === currentFieldId) {
        field.wf_id = wfId;
      }
    });
    return fields;
  };

  _getClauseField = () =>
    this.props.fields.find(
      dfield => dfield.id === this.props.entity.dynamicFieldId,
    ) || "";

  render() {
    const { closeAddWorkFlowScreen, formFields } = this.props;
    const { meta: { subtype } = {} } = this._getClauseField();
    if (this.state.showLoader) {
      return (
        <div className="document-clause-options animated slideinright tempworkflow-wrapper">
          <Loader />
        </div>
      );
    }

    return (
      <Fragment>
        <div className="document-clause-options animated slideinright tempworkflow-wrapper">
          <div className="tempworkflow-options-header">
            <span
              className="btn-icon tempworkflow-options-back-button"
              onClick={() =>
                closeAddWorkFlowScreen({ flag: false, entity: {} })
              }
              onKeyDown={() => {}}
              role="button"
              tabIndex={0}>
              <FontAwesomeIcon icon={["far", "angle-left"]} />
            </span>
            Add approval workflow
            <div className="tempworkflow-options-btns">
              <Button
                data-test="dynamic-fields-discard-button"
                className="btn-transparent"
                onClick={() =>
                  closeAddWorkFlowScreen({ flag: false, entity: {} })
                }>
                Discard
              </Button>
              <Button
                data-test="dynamic-fields-save-button"
                onClick={this._onSubmit}
                isLoading={this.state.showButtonLoader}>
                {this.props.data.rules ? "Update" : "Save"}
              </Button>
            </div>
          </div>
          <div className="tempworkflow-approvalwrapper-bodywrapper">
            {formFields.length > 0 &&
              formFields.map((data, index) => (
                <Fragment>
                  <TempWFRuleWrapper
                    data={data}
                    index={index}
                    removeRule={this._removeRuleFromWorkflow}
                  />
                  {formFields.length - 1 === index && (
                    <div
                      className={`tempworkflow-rule-action ${
                        subtype === "free_text" ? "right-align-btn" : ""
                      }`}>
                      {subtype !== "free_text" ? (
                        <AddAnotherRule
                          addRuleToWorkflow={this._addRuleToWorkflow}
                        />
                      ) : (
                        <span />
                      )}
                      <div
                        className="tempworkflow-removerule-btn"
                        onKeyPress={() => null}
                        role="button"
                        tabIndex="0"
                        onClick={() =>
                          this.setState({ showConfirmationBox: true })
                        }>
                        <i>
                          <FontAwesomeIcon icon={["far", "times-circle"]} />
                        </i>
                        Delete this workflow
                      </div>

                      {/** CONFIRMATION BOX FOR DETACHING WORKFLOW */}
                      <ConfirmationBox
                        show={this.state.showConfirmationBox}
                        closePopup={() =>
                          this.setState({ showConfirmationBox: false })
                        }
                        header="Are you sure you want to delete this workflow?"
                        checkboxLabel="Yes, I want to delete."
                        confirmAction={this._detachWorkFlow}
                        okBtnText="Delete workflow"
                      />
                    </div>
                  )}
                </Fragment>
              ))}
          </div>
        </div>
        <div className="right-pane-bg" />
      </Fragment>
    );
  }
}

const mapStateToProps = state => {
  const {
    dynamicFields: { fields },
    workflow: { formFields, workflowDetails: data, metaData, entity, users },
    template: { id: templateId, fields_used: usedFields },
  } = state.TemplateStore;

  const dynamicField = fields.find(f => f.id === entity.id);

  return {
    data,
    dynamicField,
    entity,
    fields,
    formFields,
    metaData,
    templateId,
    usedFields,
    users,
  };
};

const mapDispatchToProps = {
  addNewRuleToTemplateWF,
  closeAddWorkFlowScreen: data => toggleWorkFlowScreen(data),
  createTempWorkFlow,
  getInitData: id => getInitData(id),
  removeRuleFromTemplateWF,
  removeWorkFlowFromTemplate: (workflowId, usedFields) =>
    removeWorkFlowFromTemplate(workflowId, usedFields),
  updateTemplateWFCondition,
  updateUsedFields: fields => updateUsedFields(fields),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(TempWorkFlow);



// WEBPACK FOOTER //
// ./src/components/Template/TempWorkFlow/index.js